import React from 'react';
import { map, some, cloneDeep, noop } from 'lodash';

// import default view
import { forceScroller as ForceScrollerView } from './force-scroller.view';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';

// some static constant meta-data
const SCROLLER_META = {
    scrollerSelector: '.ui-force-scroller',
    scrollerWrapperSelector: '.ui-force-scroller__wrapper',
    scrollerContainerSelector: '.ui-force-scroller__wrapper__container',
    scrollerItemSelector: '.ui-force-scroller__wrapper__container__item',
    navButtonSize: 75
};

// default component data
const DEFAULT_DATA = {
    elementRefs: {
        scrollerWrapper: null, // scroller wrapper
        scrollerContainer: null, // items container (which moves inside wrapper)
        items: [] // scroller items
    },
    elementWidths: {
        scrollerWrapper: 0, // width of scroller wrapper
        scrollerContainer: 0, // width of items container (which moves inside wrapper)
        items: [] // width of scroller items
    },
    scrollPoints: [] // this saved numer of clicks needed and scroll width for each click
};

// default component state
const DEFAULT_STATE = {
	canAnimate: false, // do not animate util component is initialized
    isVisible: false,     // make scroller visible when all caculations are done
    lastScrolledPoint: null, // next active index of scroll points (AKA click index)
    scrollAmount: 0, // scroll width in pixels
    canMoveBackward: false, // show back button
    canMoveForward: false // show next button
};

/**
 * @type {function}
 * @desc A Higher-Order-Component to enhance functionality of `Component`
 * @param {object} Component - A React component
 * @example
 * import { forceScroller, ForceScroller } from 'modules/force-scroller';
 *
 * // create a component
 * const ComponentWithHOC = forceScroller( CustomView );
 */
const forceScroller = ( Component = ForceScrollerView ) => {
    class ForceScroller extends React.Component {
        constructor( props ) {
            log.debug( 'ForceScroller.constructor()' );
			super( props );

			// scroller DOM reference
			this.scrollerRef = React.createRef();

			// initial component data
			this.data = this.getDefaultData();

			// initial component state
			this.state = this.getDefaultState();

			// bind methods and events
			this.__bind();
        }

        getDefaultData() {
			log.debug( 'ForceScroller.getDefaultData()' );
			return cloneDeep( DEFAULT_DATA );
		}

		getDefaultState() {
			log.debug( 'ForceScroller.getDefaultState()' );
			return cloneDeep( DEFAULT_STATE );
		}

		/**
		 * @desc Bind methods and events
		 */
		__bind() {
			log.debug( 'ForceScroller.__bind()' );
			
			this.handleMoveBackward = this.handleMoveBackward.bind( this );
			this.handleMoveForward = this.handleMoveForward.bind( this );
			this.handleForceReload = this.handleForceReload.bind( this );

			// listen to force reload event
			if( this.props.messageBus ) {
				this.props.messageBus.on( MESSAGE_BUS_EVENTS.RELOAD_FORCE_SCROLLER, this.handleForceReload, this );
			}
		}

		/**
		 * @desc Unbind events
		 */
		__unbind() {
			log.debug( 'ForceScroller.__unbind()' );
			
			// listen to force reload event
			if( this.props.messageBus ) {
				this.props.messageBus.off( MESSAGE_BUS_EVENTS.RELOAD_FORCE_SCROLLER, this.handleForceReload, this );
			}
		}

		/**
		 * @desc set component state data
		 * This involes calculation of item widths, item index etc.
		 */
		setComponentDataAndState( callback ) {
			log.debug( 'ForceScroller.setComponentDataAndState()' );

			/** @type { HTMLElement } */
			const scrollerRefElem = this.scrollerRef.current;

			if( scrollerRefElem ) {

				// save reference elements //

				/** @type { HTMLElement } */
				const scrollerWrapperElem = scrollerRefElem.querySelector( SCROLLER_META.scrollerWrapperSelector );

				/** @type { HTMLElement } */
				const scrollerContainerElem = scrollerRefElem.querySelector( SCROLLER_META.scrollerContainerSelector );

				/** @type { HTMLElement[] } */
				const itemElems = scrollerRefElem.querySelectorAll( SCROLLER_META.scrollerItemSelector );

				this.data.elementRefs = { scrollerWrapper: scrollerWrapperElem, scrollerContainer: scrollerContainerElem, items: itemElems };

				/*----------------------*/

				// save element widths //
				const scrollerWrapperWidth = scrollerWrapperElem.offsetWidth;
				const scrollerContainerWidth = scrollerContainerElem.scrollWidth;
				const lastItemIndex = itemElems.length - 1;
				const itemWidths = map( itemElems, ( itemElem, itemIndex ) => {
					let _returnValue = 0;
					if ( itemIndex < lastItemIndex ) {
						_returnValue = itemElem.offsetWidth + this.props.gutter;
					} else {
						_returnValue = itemElem.offsetWidth + this.props.shiftBuffer;
					}
					return _returnValue;
				} );
				this.data.elementWidths = { scrollerWrapper: scrollerWrapperWidth, scrollerContainer: scrollerContainerWidth, items: itemWidths };

				/*----------------------*/

				// calculate scroll points

				let _widthSum = 0; // counter to accumulate sum of widths
				let itemOnWrapperEdgeIndex = null; // find element which is on the edge (right) of the wrapper
				let itemOnWrapperEdgeScrollWidth = 0; // width of above item lying outside wrapper

				// loop on item widths using `some` to break when item is found
				some( this.data.elementWidths.items, ( width, itemIndex ) => {
					_widthSum = _widthSum + width;

					if( _widthSum === this.data.elementWidths.scrollerWrapper ) {
						itemOnWrapperEdgeIndex = itemIndex + 1;
						itemOnWrapperEdgeScrollWidth = width;

						return true; // break
					} else if( _widthSum > this.data.elementWidths.scrollerWrapper ) {
						itemOnWrapperEdgeIndex = itemIndex;
						itemOnWrapperEdgeScrollWidth = _widthSum - this.data.elementWidths.scrollerWrapper;
						return true; // break
					}

					return false; // continue
				} );

				// items to move moved (scrolled)
				if( null !== itemOnWrapperEdgeIndex ) {
					const scrollableItemWidths = this.data.elementWidths.items.slice( itemOnWrapperEdgeIndex );

					// create scroll points
					this.data.scrollPoints = map( scrollableItemWidths, ( scrollableItemWidth, clickIndex ) => {
						if( 0 === clickIndex ) {
							return itemOnWrapperEdgeScrollWidth;
						} else {
							return scrollableItemWidth;
						}
					} );
				}

				/*----------------------*/

				// update component state
				this.setState( {
					isVisible: true,
					canAnimate: true,
					canMoveForward: 0 < this.data.scrollPoints.length
				}, callback );
			}

		}

		/**
		 * @desc handler for back button press ( moving backward )
		 */
		handleMoveBackward( event ) {
			log.debug( 'ForceScroller.handleMoveBackward()' );

			event.preventDefault();
			event.stopPropagation();

			if( true === this.state.canMoveBackward ) {
				const { scrollPoints } = this.data;
				const { lastScrolledPoint, scrollAmount } = this.state;
				const newLastScrollPoint = ( 0 === lastScrolledPoint ) ? null : lastScrolledPoint - 1;

				const nextState = {
					lastScrolledPoint: newLastScrollPoint,
					scrollAmount: scrollAmount - scrollPoints[ lastScrolledPoint ],
					canMoveBackward: ( null !== newLastScrollPoint ),
					canMoveForward: true
				};

				this.setState( nextState, () => this.props.onScrollChange( this.state ) );
			}
		}

		/**
		 * @desc handler for next button press ( moving forward )
		 */
		handleMoveForward( event ) {
			log.debug( 'ForceScroller.handleMoveForward()' );

			event.preventDefault();
			event.stopPropagation();

			if( true === this.state.canMoveForward ) {
				const { scrollPoints } = this.data;
				const { lastScrolledPoint, scrollAmount } = this.state;
				const newLastScrollPoint = ( null === lastScrolledPoint ) ? 0 : lastScrolledPoint + 1;
				const isLastScrollPoint = ( ( scrollPoints.length - 1 ) === newLastScrollPoint );

				const nextState = {
					lastScrolledPoint: newLastScrollPoint,
					scrollAmount: scrollAmount + scrollPoints[ newLastScrollPoint ],
					canMoveBackward: true,
					canMoveForward: ! isLastScrollPoint
				};

				this.setState( nextState, () => this.props.onScrollChange( this.state ) );
			}
		}

		/**
		 * @desc force reload the scroll points
		 */
		handleForceReload() {
			log.debug( 'ForceScroller.handleForceReload()' );

			// reset component data
			this.data = this.getDefaultData();
			this.setState( this.getDefaultState(), () => {
				this.setComponentDataAndState( () => {
					this.props.onScrollChange( this.state );
				} );
			} );
		}

		/********************************************************************/

		/**
		 * @desc When component mounts, calculate width of each child item
		 */
		componentDidMount() {
			log.debug( 'ForceScroller.componentDidMount()' );

			// set component data
			this.setComponentDataAndState( () => {
				log.debug( 'setComponentDataAndState()' );
			} );

		}

		/**
		 * @desc Update scroller
		 */
		componentDidUpdate() {
			log.debug( 'ForceScroller.componentDidUpdate()' );

			/** @type { HTMLElement } */
			const scrollerRefElem = this.scrollerRef.current;

			if( scrollerRefElem ) {

				/** @type { HTMLElement[] } */
				const itemElems = scrollerRefElem.querySelectorAll( SCROLLER_META.scrollerItemSelector );

				if( itemElems.length !== this.data.elementRefs.items.length ) {
					this.data = this.getDefaultData();
					// eslint-disable-next-line react/no-did-update-set-state
					this.setState( this.getDefaultState(), () => {
						this.setComponentDataAndState();
					} );
				}
			}
		}

		/**
		 * @desc When component unmounts, reload component
		 */
		componentWillUnmount() {
			this.__unbind();
		}

		/**
		 * @desc Render force scroller wrapper
		 */
		render() {
			log.debug( 'ForceScroller.render()' );
			return (
				<Component
					{ ...this.props }
					ref={ this.scrollerRef }
					canAnimate={ this.state.canAnimate }
					isVisible={ this.state.isVisible }
					scrollAmount={ this.state.scrollAmount }
					expandColumns={ this.props.expandColumns }
					canMoveBackward={ this.state.canMoveBackward }
					canMoveForward={ this.state.canMoveForward }
					moveBackward={ this.handleMoveBackward }
					moveForward={ this.handleMoveForward }
				/>
			);
		}
    }

    // set display name
    ForceScroller.displayName = 'ForceScroller';

    // default props
    ForceScroller.defaultProps = {
		gutter: 20,
		shiftBuffer: 0,
		scrollAnimationDuration: 300,
		onScrollChange: noop,
		expandColumns: false,
	};

    // prop types
    ForceScroller.propTypes = {};

    // return component
    return ForceScroller;
};

// export HOC
export { forceScroller };

// export react component with default view
export const ForceScroller = forceScroller();
